import { gql } from "@apollo/client";
import MultiValuesField from "controls/MultiValuesField";
import useIsAdmin from "hooks/useIsAdmin";
import DateTimeInput from "inputs/DateTimeInput";
import SelectInput from "inputs/SelectInput";
import SwitchInput from "inputs/SwitchInput";
import TextInput from "inputs/TextInput";
import React, { useMemo, useState } from "react";
import ReadminObjectInput from "readmin_pages/shared/ReadminObjectInput";

export const LiveStreamsFormFragment = gql`
  fragment LiveStreamsFormFragment on LiveStream {
    id
    name
    court {
      id
      org {
        allowEncodeHighQualityOutputs
      }
    }
    adPreset {
      id
    }
    outputResolution
    cancelled
    startAt
    finishAt
    motioncropEnabled
    logoVisible
  }
`;

export default function LiveStreamsForm({ liveStreams }) {
  liveStreams = liveStreams?.map((liveStream) => ({
    motioncropEnabled: true,
    logoVisible: true,
    outputResolution: "720p",
    ...liveStream,
  }));

  const isAdmin = useIsAdmin();
  const [allowEncodeHighQualityOutputs, allowEncodeHighQualityOutputsSet] = useState(
    liveStreams?.some((liveStream) => liveStream.court?.org?.allowEncodeHighQualityOutputs),
  );
  const [startAt, startAtSet] = useState(liveStreams?.[0]?.startAt || null);
  const [finishAt, finishAtSet] = useState(liveStreams?.[0]?.finishAt || null);

  const durationError = useMemo(() => {
    if (!startAt || !finishAt) return null;
    const durationMinutes = (new Date(finishAt) - new Date(startAt)) / 60000;
    if (durationMinutes < 30 || durationMinutes > 240)
      return "Live Stream duration must be between 30 minutes and 4 hours.";
    return null;
  }, [startAt, finishAt]);

  return (
    <>
      <MultiValuesField
        label="Name"
        name="name"
        required
        defaultValues={liveStreams?.map((liveStream) => liveStream.name)}
        input={<TextInput />}
      />
      <MultiValuesField
        label="Court"
        name="court"
        required
        defaultValues={liveStreams?.map((liveStream) => liveStream.court)}
        onChange={() =>
          allowEncodeHighQualityOutputsSet([
            liveStreams?.some((liveStream) => liveStream.court?.org?.allowEncodeHighQualityOutputs),
          ])
        }
        input={
          <ReadminObjectInput
            optionsTypename="Court"
            optionsFilters={{
              allow_live_streams: true,
            }}
          />
        }
      />
      <MultiValuesField
        label="Start At"
        name="startAt"
        required
        defaultValues={liveStreams?.map((liveStream) => liveStream.startAt)}
        input={<DateTimeInput />}
        onChange={(values) => startAtSet(values[0])}
      />
      <MultiValuesField
        label="Finish At"
        name="finishAt"
        required
        defaultValues={liveStreams?.map((liveStream) => liveStream.finishAt)}
        input={<DateTimeInput />}
        onChange={(values) => finishAtSet(values[0])}
        error={!!durationError}
        helperText={durationError}
      />
      <MultiValuesField
        label="Cancelled"
        name="cancelled"
        defaultValues={liveStreams?.map((liveStream) => liveStream.cancelled)}
        input={<SwitchInput />}
      />
      <MultiValuesField
        label="Ad Group"
        name="adPreset"
        defaultValues={liveStreams?.map((liveStream) => liveStream.adPreset)}
        input={<ReadminObjectInput optionsTypename="AdPreset" />}
      />
      {(isAdmin || allowEncodeHighQualityOutputs) && (
        <MultiValuesField
          label="Output Resolution"
          name="outputResolution"
          required
          defaultValues={liveStreams?.map((liveStream) => liveStream.outputResolution)}
          input={<SelectInput options={["1080p", "720p"]} />}
        />
      )}
      {isAdmin && (
        <>
          <MultiValuesField
            label="Motioncrop Enabled"
            name="motioncropEnabled"
            defaultValues={liveStreams?.map((liveStream) => liveStream.motioncropEnabled)}
            input={<SwitchInput />}
          />
          <MultiValuesField
            label="Logo Visible"
            name="logoVisible"
            defaultValues={liveStreams?.map((liveStream) => liveStream.logoVisible)}
            input={<SwitchInput />}
          />
        </>
      )}
    </>
  );
}
